// Phase 24D — Column visibility popover
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Columns3, RotateCcw, Save } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

export default function ColumnVisibilityMenu({ reportKey, columns, onChange }) {
  const [visible, setVisible] = useState(null); // null = all
  const allKeys = columns.map(c => c.key);

  useEffect(() => {
    api.get("/reports/column-visibility", { params: { report_key: reportKey } })
       .then(r => {
         const v = r.data?.visible_columns;
         setVisible(v || allKeys);
         onChange && onChange(v || allKeys);
       }).catch(() => { setVisible(allKeys); onChange && onChange(allKeys); });
    // eslint-disable-next-line
  }, [reportKey]);

  function toggle(k) {
    const next = visible.includes(k) ? visible.filter(x => x !== k) : [...visible, k];
    setVisible(next); onChange && onChange(next);
  }
  async function save() {
    try {
      await api.put("/reports/column-visibility", { report_key: reportKey, visible_columns: visible });
      toast.success("Column visibility saved");
    } catch (e) { toast.error(formatApiError(e)); }
  }
  async function reset() {
    try {
      await api.delete("/reports/column-visibility", { params: { report_key: reportKey } });
      setVisible(allKeys); onChange && onChange(allKeys);
      toast.success("Reset to defaults");
    } catch (e) { toast.error(formatApiError(e)); }
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button size="sm" variant="outline" data-testid="col-vis-trigger">
          <Columns3 className="w-4 h-4 mr-1" /> Columns
          {visible && visible.length !== columns.length && (
            <span className="ml-1 text-[10px] text-neutral-500">({visible.length}/{columns.length})</span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 max-h-96 overflow-y-auto" align="end" data-testid="col-vis-popover">
        <div className="text-xs font-semibold mb-2">Show columns</div>
        {columns.map(c => (
          <label key={c.key} className="flex items-center gap-2 text-sm py-1 cursor-pointer">
            <input type="checkbox" checked={visible?.includes(c.key) ?? true}
                     onChange={() => toggle(c.key)}
                     data-testid={`col-vis-${c.key}`} />
            {c.label}
            {c.admin_only && <span className="ml-1 text-[10px] text-orange-600">admin</span>}
          </label>
        ))}
        <div className="flex gap-2 mt-2 pt-2 border-t">
          <Button size="sm" variant="outline" className="flex-1" onClick={reset}
                    data-testid="col-vis-reset">
            <RotateCcw className="w-3 h-3 mr-1" /> Reset
          </Button>
          <Button size="sm" className="flex-1" onClick={save} data-testid="col-vis-save">
            <Save className="w-3 h-3 mr-1" /> Save
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
